import { Injectable } from '@angular/core';

import { Observable, timer, forkJoin, empty, of } from 'rxjs';
import { map, exhaustMap, retryWhen, delay, take, tap } from 'rxjs/operators';

import { TasksListResponse, Status } from './order.model';
import { Order } from './order.model';
import { ServiceType } from 'app/core/service-type.model';
import { Api } from 'app/core/api.service';
import { camelCaseObject } from 'app/core/case-transform';
import { UserSettingsService } from 'app/user/user-settings.service';

export interface OrderList {
    count: number;
    orders: Order[];
    statuses?: TaskStatusOverview[];
}

export interface OrderListStatuses {
    [status: string]: number;
}

export interface OrderListFilter {
    serviceType: ServiceType;
    status?: string;
    skip: number;
    take: number;
}

export interface OrderTextFilter {
    serviceType: ServiceType;
    text: string;
    skip: number;
    take: number;
}

export interface TaskStatusOverview {
    status: string;
    taskCount: number;
    documentsCount: number;
    pagesCount: number;
}

@Injectable()
export class OrderListService {

    constructor(private _api: Api, private _userSettingsService: UserSettingsService) {}

    /** Возвращает заказы пользователя по фильтру (с заданным типом FilterType и status) */
    poolByStatus(filter: OrderListFilter): Observable<OrderList> {
      return timer(0, 5000)
        .pipe(
          exhaustMap(() => forkJoin(
              this.getByStatus(filter),
              this._getStatusesOverview(filter.serviceType)
            )
          ),
          map(([orders, statuses]) => ({ ...orders, statuses })),
          retryWhen(errors => errors.pipe(tap(console.error), delay(10000), take(10)))
        );
    }

    pool(filter: OrderListFilter): Observable<OrderList> {
      return timer(0, 10000)
        .pipe(
          exhaustMap(() => forkJoin(
            this.get(filter),
            this._getStatusesOverview(filter.serviceType)
            )
          ),
          map(([orders, statuses]) => ({ ...orders, statuses })),
          retryWhen(errors => errors.pipe(tap(console.error), delay(10000), take(10)))
        );
    }

    hasOrders(): Observable<boolean> {
      return timer(0, 5000)
        .pipe(
            exhaustMap(() => this._getStatusesOverview(this._userSettingsService.serviceType)),
            map(statuses => statuses.some(status => status.taskCount > 0))
        );
    }

    get(filter: OrderListFilter): Observable<OrderList> {
      const { serviceType, skip, take } = filter;
      const params = { skip, take };

      return this._api
        .get(`/v2/task/${serviceType}/search`, { params })
        .pipe(
          map((response: TasksListResponse) => {
            if (response && response.tasks) {
              return {
                count: response.count,
                orders: response.tasks.map(order => new Order(order))
              };
            }

            return {
              count: 0,
              orders: []
            };
          })
        )
    }

    /** Загружает отфильтрованные заказы по типу и статусу */
    getByStatus(filter: OrderListFilter): Observable<OrderList> {
        const { serviceType, status, skip, take } = filter;
        const params = { skip, take };
        return forkJoin(
          this._api
            .get(`/v2/task/${serviceType}/search/${status}`, { params }),
          status === Status.InProgress
            ? this._api.get(`/v2/task/${serviceType}/search/${Status.Submitted}`, { params })
            : of({ count: 0, tasks: [] })
        )
          .pipe(
            map((responses: TasksListResponse[] | null) => {
              if (responses && responses[0] && responses[0].tasks && responses[1] && responses[1].tasks) {
                return {
                    count: responses[0].count + responses[1].count,
                    orders: responses[0].tasks
                      .concat(responses[1].tasks)
                      .sort((a,b) => Date.parse(b.created) - Date.parse(a.created))
                      .map(order => new Order(order))
                };
              }
              return { count: 0, orders: [] };
            })
          );
    }

    _getStatusesOverview(serviceType: ServiceType): Observable<TaskStatusOverview[]> {
        return this._api
            .get(`/v2/task/${serviceType}/status/overview`)
            .pipe(map(response => (response || []).map(camelCaseObject)));
    }
}
